/**
 * Returns back a NodeList of focusable elements
 * that exist within the passed parent HTMLElement, or
 * an empty array if no parent passed.
 *
 * @param {HTMLElement} parent HTML element
 * @returns {(NodeList|Array)} The focusable elements that we can find
 */
const getAllFocusableElements = (parent) => {
  if (!parent || !(parent instanceof HTMLElement)) {
    console.warn('You need to pass a parent HTMLElement');
    return [];
  }

  return parent.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
  );
};

/**
 * Traps focus within a given set of focusable elements.
 * Prevents focus from escaping when pressing "Tab" or "Shift + Tab".
 *
 * @param {KeyboardEvent} event The keyboard event triggered by user interaction
 * @param {NodeList|Array} focusableElements A list of focusable elements within a container
 */
const focusTrap = (event, focusableElements) => {
  if (!focusableElements?.length) return;

  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  if (event.key !== 'Tab') return;

  if (event.shiftKey && document.activeElement === firstFocusableElement) {
    event.preventDefault();
    lastFocusableElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
    event.preventDefault();
    firstFocusableElement.focus();
  }
};

/**
 * Focuses the first input, select, or textarea element within the given HTMLElement.
 *
 * @param {HTMLElement} element - The parent element to search within.
 * @returns {void}
 */
const focusElement = (element) => {
  if (!element || !(element instanceof HTMLElement)) {
    console.warn('You need to pass an HTMLElement');
    return;
  }

  return element.querySelector('input:not([disabled]), select:not([disabled]), textarea:not([disabled])')?.focus();
};

export {getAllFocusableElements, focusTrap, focusElement};
